import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import REGISTERED_THEMES from 'app/styles/themes';
import { DEFAULT_VALUES } from 'app/config/urlParams.config';
import ConfigContext from 'app/common/ConfigContext';

import { applyTheme, getDesignVersion, getTheme, getThemeName } from './utils';

interface ContextValue {
  theme: WidgetTheme;
  designVersion: DesignVersion;
}

const ThemeContext = createContext<ContextValue>({
  theme: REGISTERED_THEMES[DEFAULT_VALUES.theme],
  designVersion: DEFAULT_VALUES.designVersion
});

const ThemeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const config = useContext(ConfigContext);
  const { customer } = config;
  const themeName = useMemo(() => getThemeName(config), []);
  const designVersion = getDesignVersion(customer);
  const theme = getTheme(themeName, REGISTERED_THEMES);

  const appliedTheme = useMemo(() => {
    applyTheme(themeName, theme);
    return { theme, designVersion };
  }, [themeName, theme, designVersion]);

  return <ThemeContext.Provider value={appliedTheme}>{children}</ThemeContext.Provider>;
};

ThemeContextProvider.displayName = 'ThemeContextProvider';
ThemeContext.displayName = 'ThemeContext';

export default ThemeContext;
export { ThemeContextProvider };
